<script setup>
import { XMarkIcon } from '@heroicons/vue/24/outline';
import { computed, watch } from 'vue';

const props = defineProps({
    show: {
        type: Boolean,
        default: false,
    },
    maxWidth: {
        type: String,
        default: '7xl',
    },
    closeable: {
        type: Boolean,
        default: true,
    },
});

const emit = defineEmits(['closes']);

watch(
    () => props.show,
    () => {
        if (props.show) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = null;
        }
    }
);

const close = () => {
    if (props.closeable) {
        emit('closes');
    }
};

const maxWidthClass = computed(() => {
    return {
        sm: 'sm:tw-max-w-sm',
        md: 'sm:tw-max-w-md',
        lg: 'sm:tw-max-w-lg',
        xl: 'sm:tw-max-w-xl',
        '2xl': 'sm:tw-max-w-2xl',
        '3xl': 'sm:tw-max-w-3xl',
        '4xl': 'sm:tw-max-w-4xl',
        '5xl': 'sm:tw-max-w-5xl',
        '6xl': 'sm:tw-max-w-6xl',
        '7xl': 'sm:tw-max-w-7xl',
    }[props.maxWidth];
});
</script>

<template>
    <Teleport to="body">
        <Transition leave-active-class="tw-duration-200">
            <div v-show="show"
                class="max-sm:tw-p-0 tw-fixed tw-inset-0 tw-overflow-y-auto tw-px-4 tw-py-6 sm:tw-px-0 tw-z-[999999]"
                scroll-region>
                <Transition enter-active-class="tw-ease-out tw-duration-300" enter-from-class="tw-opacity-0"
                    enter-to-class="tw-opacity-100" leave-active-class="duration-0">
                    <div v-show="show" class="tw-fixed tw-inset-0 tw-transform tw-transition-all" @click="close">
                        <div class="tw-absolute tw-inset-0 tw-bg-gray-500 tw-opacity-75" />
                    </div>
                </Transition>

                <Transition enter-active-class="tw-ease-out tw-duration-300"
                    enter-from-class="tw-opacity-0 tw-translate-y-4 sm:tw-translate-y-0 sm:tw-scale-95"
                    enter-to-class="tw-opacity-100 tw-translate-y-0 sm:tw-scale-100" leave-active-class="duration-0"
                    leave-from-class="tw-opacity-100 tw-translate-y-0 sm:tw-scale-100"
                    leave-to-class="tw-opacity-0 tw-translate-y-4 sm:tw-translate-y-0 sm:tw-scale-95">
                    <div v-show="show"
                        class="tw-bg-white sm:tw-rounded-lg tw-overflow-hidden tw-shadow-xl tw-transform tw-transition-all sm:tw-w-full sm:tw-mx-auto tw-w-full tw-h-full tw-shadow-xl"
                        :class="maxWidthClass">

                        <div class="tw-border-b tw-border-gray-200 tw-bg-white tw-px-4 tw-py-5 sm:tw-px-6 tw-h-100">
                            <div
                                class="-tw-ml-4 -tw-mt-4 tw-flex tw-flex-wrap tw-items-center tw-justify-between sm:tw-flex-nowrap">
                                <div class="tw-ml-4 tw-mt-4">
                                    <slot name="headers" />
                                </div>
                                <div class="tw-ml-4 tw-mt-4 tw-flex-shrink-0">
                                    <button @click="close"
                                        class="tw-text-gray-400 hover:tw-text-gray-800 tw-bg-transparent">
                                        <XMarkIcon class="tw-w-6" />
                                    </button>
                                </div>
                            </div>
                        </div>
                        <slot />
                    </div>
                </Transition>
            </div>
        </Transition>
    </Teleport>
</template>
